import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Label,
  Pagination,
  Popup,
  Table,
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../helpers';
import { Link } from 'react-router-dom';
import { ITEMS_PER_PAGE } from '../../constants';

const ModelGroup = () => {
  const [modelGroups, setModelGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');

  const loadModelGroups = async (startIdx) => {
    try {
      const res = await API.get(`/api/model_group/?p=${startIdx}`);
      const { success, message, data } = res.data;
      if (success) {
        if (startIdx === 0) {
          setModelGroups(data || []);
        } else {
          setModelGroups((prev) => [...prev, ...(data || [])]);
        }
      } else {
        showError(message);
      }
    } catch (error) {
      showError(error.message);
    }
    setLoading(false);
  };

  const onPaginationChange = (e, { activePage }) => {
    (async () => {
      if (activePage === Math.ceil(modelGroups.length / ITEMS_PER_PAGE) + 1) {
        await loadModelGroups(activePage - 1);
      }
      setActivePage(activePage);
    })();
  };

  useEffect(() => {
    loadModelGroups(0).then();
  }, []);

  const deleteModelGroup = async (id) => {
    const res = await API.delete(`/api/model_group/${id}`);
    const { success, message } = res.data;
    if (success) {
      showSuccess('删除成功');
      await loadModelGroups(0);
      setActivePage(1);
    } else {
      showError(message);
    }
  };

  const toggleStatus = async (id, status) => {
    const group = modelGroups.find((g) => g.id === id);
    if (!group) return;
    const newStatus = status === 1 ? 2 : 1;
    const res = await API.put(`/api/model_group/`, {
      ...group,
      status: newStatus,
    });
    const { success, message } = res.data;
    if (success) {
      showSuccess('操作成功');
      await loadModelGroups(0);
    } else {
      showError(message);
    }
  };

  const renderModels = (models) => {
    if (!models) return '-';
    const modelList = models.split(',').filter((m) => m.trim());
    if (modelList.length === 0) return '-';
    if (modelList.length <= 3) {
      return modelList.map((m, i) => (
        <Label key={i} size='small' style={{ margin: '1px' }}>
          {m.trim()}
        </Label>
      ));
    }
    return (
      <Popup
        trigger={
          <span>
            {modelList.slice(0, 3).map((m, i) => (
              <Label key={i} size='small' style={{ margin: '1px' }}>
                {m.trim()}
              </Label>
            ))}
            <Label size='small' color='grey'>
              +{modelList.length - 3}
            </Label>
          </span>
        }
        content={modelList.join(', ')}
        position='top center'
      />
    );
  };

  const renderStatus = (status) => {
    return status === 1 ? (
      <Label color='green'>已启用</Label>
    ) : (
      <Label color='grey'>已禁用</Label>
    );
  };

  const searchModelGroups = async () => {
    if (searchKeyword === '') {
      await loadModelGroups(0);
      return;
    }
    setLoading(true);
    try {
      const res = await API.get(`/api/model_group/?p=0`);
      const { success, data } = res.data;
      if (success) {
        const filtered = (data || []).filter(
          (g) =>
            g.name.toLowerCase().includes(searchKeyword.toLowerCase()) ||
            (g.models &&
              g.models.toLowerCase().includes(searchKeyword.toLowerCase()))
        );
        setModelGroups(filtered);
      }
    } catch (error) {
      showError(error.message);
    }
    setLoading(false);
  };

  const handleKeywordChange = async (e, { value }) => {
    setSearchKeyword(value.trim());
  };

  const sortedGroups = modelGroups.slice(
    (activePage - 1) * ITEMS_PER_PAGE,
    activePage * ITEMS_PER_PAGE
  );

  return (
    <div className='dashboard-container'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header>模型组管理</Card.Header>
          <Form.Input
            icon='search'
            placeholder='搜索模型组名称...'
            value={searchKeyword}
            loading={loading}
            onChange={handleKeywordChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                searchModelGroups().then();
              }
            }}
            style={{ marginTop: '10px' }}
          />
          <Table basic='very' compact>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>名称</Table.HeaderCell>
                <Table.HeaderCell>描述</Table.HeaderCell>
                <Table.HeaderCell>模型列表</Table.HeaderCell>
                <Table.HeaderCell>状态</Table.HeaderCell>
                <Table.HeaderCell>操作</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedGroups.map((group) => (
                <Table.Row key={group.id}>
                  <Table.Cell>
                    <strong>{group.name}</strong>
                  </Table.Cell>
                  <Table.Cell>{group.description || '-'}</Table.Cell>
                  <Table.Cell>{renderModels(group.models)}</Table.Cell>
                  <Table.Cell>{renderStatus(group.status)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      size='small'
                      onClick={() => toggleStatus(group.id, group.status)}
                    >
                      {group.status === 1 ? '禁用' : '启用'}
                    </Button>
                    <Button
                      size='small'
                      as={Link}
                      to={`/model-group/edit/${group.id}`}
                    >
                      编辑
                    </Button>
                    <Button
                      size='small'
                      negative
                      onClick={() => {
                        if (window.confirm('确定要删除此模型组吗？')) {
                          deleteModelGroup(group.id).then();
                        }
                      }}
                    >
                      删除
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
            <Button as={Link} to='/model-group/add' positive>
              添加模型组
            </Button>
            <Pagination
              activePage={activePage}
              onPageChange={onPaginationChange}
              size='small'
              siblingRange={1}
              totalPages={
                Math.ceil(modelGroups.length / ITEMS_PER_PAGE) +
                (modelGroups.length % ITEMS_PER_PAGE === 0 ? 1 : 0)
              }
            />
          </div>
        </Card.Content>
      </Card>
    </div>
  );
};

export default ModelGroup;
